import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, avatarColor, initials, photoSrc } from "../sectionUtils";
import s from "./SectionRosterTab.module.css";

const STATUS_STYLE = {
  ENROLLED:  { bg: "#e8f6ed", color: "#1e6f3e" },
  PENDING:   { bg: "#fdf3d4", color: "#8a6d28" },
  GRADUATED: { bg: "#e8eef9", color: "#2b4d8a" },
  INACTIVE:  { bg: "#f0f0f0", color: "#777" },
};

export default function SectionRosterTab({ section, onViewStudent }) {
  const [students, setStudents] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [search,   setSearch]   = useState("");

  useEffect(() => {
    if (!section?.id) return;
    let cancelled = false;
    setLoading(true); setError("");

    fetch(`${API_BASE}/api/students?sectionId=${section.id}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!cancelled) setStudents(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) setError("Could not load roster."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [section?.id]);

  const filtered = students
    .filter(st => {
      const q = search.toLowerCase().trim();
      if (!q) return true;
      const hay = [st.firstName, st.middleName, st.lastName, st.studentNumber]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    })
    .sort((a, b) => {
      const la = (a.lastName || "").toLowerCase();
      const lb = (b.lastName || "").toLowerCase();
      return la < lb ? -1 : la > lb ? 1 : 0;
    });

  if (loading) return <div className={s.loading}>Loading roster…</div>;
  if (error)   return <div className={s.errorBanner}>{error}</div>;

  return (
    <div className={s.root}>
      <div className={s.toolbar}>
        <div className={s.searchWrap}>
          <span className={s.searchIcon} aria-hidden="true">🔍</span>
          <input
            className={s.searchInput}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or number…"
            aria-label="Search roster"
          />
          {search && (
            <button className={s.clearBtn} onClick={() => setSearch("")} aria-label="Clear">✕</button>
          )}
        </div>
        <span className={s.countPill}>
          {filtered.length} / {students.length} student{students.length !== 1 ? "s" : ""}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className={s.empty}>
          <div className={s.emptyIcon}>👥</div>
          <div className={s.emptyText}>
            {search ? "No students match your search." : "No students enrolled in this section."}
          </div>
        </div>
      ) : (
        <div className={s.list}>
          {filtered.map((st, idx) => {
            const photo = photoSrc(st.photoUrl);
            const name  = [st.lastName, st.firstName, st.middleName].filter(Boolean).join(", ");
            const badge = STATUS_STYLE[st.status] || { bg: "#f0f0f0", color: "#777" };
            return (
              <div
                key={st.id}
                className={s.row}
                style={{ animationDelay: `${Math.min(idx, 14) * 18}ms` }}
                onClick={() => onViewStudent?.(st)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onViewStudent?.(st); }}}
                role={onViewStudent ? "button" : undefined}
                tabIndex={onViewStudent ? 0 : undefined}
                aria-label={onViewStudent ? `View ${name}` : undefined}>

                <div
                  className={s.avatar}
                  style={photo
                    ? { backgroundImage: `url(${photo})` }
                    : { backgroundColor: avatarColor(name) }
                  }
                  aria-hidden="true">
                  {!photo && initials(st.firstName, st.lastName)}
                </div>

                <div className={s.rowInfo}>
                  <div className={s.rowName}>{name || "(Unnamed)"}</div>
                  {st.studentNumber && (
                    <div className={s.rowNum}>{st.studentNumber}</div>
                  )}
                </div>

                <span
                  className={s.statusBadge}
                  style={{ background: badge.bg, color: badge.color }}>
                  {st.status || "—"}
                </span>

                {onViewStudent && (
                  <span className={s.viewArrow} aria-hidden="true">›</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
